import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/lib/api';
import { QUERY_KEYS } from '@/lib/constants';
import type { MFAConfig, MFAConfigureResponse, MFAVerifyRequest, MFAVerifyResponse } from '@/lib/api/settings/types';
import { MFA_DEFAULT_TTL_DAYS, MFA_METHODS } from '../constants/mfa';

/**
 * Fetch list of MFA configurations
 */
export function useMFAList() {
    return useQuery({
        queryKey: [QUERY_KEYS.MFA_LIST],
        queryFn: async (): Promise<MFAConfig[]> => {
            const response = await authApi.get<MFAConfig[]>('v1/mfa/list');
            return response.data ?? [];
        },
    });
}

/**
 * Configure MFA (get TOTP URI for authenticator app setup)
 */
export function useConfigureMFA() {
    return useMutation({
        mutationFn: async (): Promise<MFAConfigureResponse> => {
            const response = await authApi.get<MFAConfigureResponse>(
                `v1/mfa/configure?method=${encodeURIComponent(MFA_METHODS.AUTHENTICATOR_APP)}`
            );
            return response.data;
        },
    });
}

/**
 * Verify MFA OTP code during setup
 */
export function useVerifyMFA() {
    return useMutation({
        mutationFn: async ({
            verifyRoute,
            totp,
            name,
        }: {
            verifyRoute: string;
            totp: string;
            name: string;
        }): Promise<MFAVerifyResponse> => {
            const payload: MFAVerifyRequest = {
                totp,
                ttl_days: MFA_DEFAULT_TTL_DAYS,
                ttl_start: Math.floor(Date.now() / 1000), // Unix timestamp in seconds
                name,
            };

            // API returns route with /api/ prefix, but authApi base URL already includes /api
            // So we need to strip the prefix to avoid double /api/api/
            const cleanRoute = verifyRoute.startsWith('/api/') ? verifyRoute.slice(5) : verifyRoute;
            const response = await authApi.post<MFAVerifyResponse>(cleanRoute, payload);
            return response.data;
        },
    });
}

/**
 * Enable MFA configuration with a name
 */
export function useEnableMFA() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ configId, name }: { configId: number; name: string }) => {
            const response = await authApi.post(`v1/mfa/config/${configId}/enable`, { name });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MFA_LIST] });
        },
    });
}

/**
 * Deactivate all MFA configurations
 */
export function useDeactivateMFA() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            const response = await authApi.get('v1/mfa/deactivate');
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MFA_LIST] });
        },
    });
}

/**
 * Rename an MFA configuration
 */
export function useRenameMFA() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ configId, name }: { configId: number; name: string }) => {
            const response = await authApi.post(`v1/mfa/config/${configId}/rename`, { name });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MFA_LIST] });
        },
    });
}

/**
 * Delete an MFA configuration
 */
export function useDeleteMFA() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (configId: number) => {
            const response = await authApi.post('v1/mfa/config/delete', { config_id: configId });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MFA_LIST] });
        },
    });
}
